import { Injectable } from '@angular/core';
import { Actions, ofActionDispatched, Store } from '@ngxs/store';
import { catchError, forkJoin, map, Observable, of, shareReplay, switchMap, tap, throwError } from 'rxjs';
import { DataSlice, SlicePreviewSeries } from 'src/app/state/library/library.model';
import { LibraryAction } from 'src/app/state/library/library.action';
import { LibraryState } from 'src/app/state/library/library.state';
import { Auth } from 'src/app/state/auth/auth.action';
import { LIBRARY_STATE } from 'src/app/state';
import { LibrarySliceApiService } from './library-slice-api.service';
import { isLibrarySliceApiPersistence } from '../../config/library-slice.config';

@Injectable({ providedIn: 'root' })
export class LibrarySlicePersistenceService {

  /** Shared load of the current user's slices; null until started, after logout or after a failed load. */
  private _loaded$: Observable<void> | null = null;

  /** Bumped on logout so a load started for the previous user never lands in the store. */
  private _session = 0;

  constructor(
    private store: Store,
    private api: LibrarySliceApiService,
    actions$: Actions,
  ) {
    actions$.pipe(ofActionDispatched(Auth.Logout)).subscribe(() => {
      this._session++;
      this._loaded$ = null;
      this._clearLocalStorage();
    });
  }

  get isApiMode(): boolean {
    return isLibrarySliceApiPersistence();
  }

  /**
   * (Re)load slices from the API and migrate any local-only slices. Never errors.
   * No-op in local mode. Used by the library page to get fresh data on every visit.
   */
  hydrate(): Observable<void> {
    return this._startLoad().pipe(
      catchError((error) => {
        console.warn('[LibrarySlicePersistence] Hydrate failed', error);
        return of(undefined);
      }),
    );
  }

  /**
   * Load slices once per login (API mode). Errors when the load fails, so callers
   * that depend on an up-to-date store — the server toggle — don't act on stale state.
   */
  ensureLoaded(): Observable<void> {
    if (!this.isApiMode) {
      return of(undefined);
    }
    return this._loaded$ ?? this._startLoad();
  }

  toggle(slice: DataSlice): Observable<{ added: boolean }> {
    // Intent follows what the UI showed at click time (save vs. remove).
    const add = !this._hasSignature(slice.signature);

    if (!this.isApiMode) {
      this.store.dispatch(new LibraryAction.ToggleSlice(slice));
      return of({ added: add });
    }

    // The server toggle removes a slice whose signature already exists. Load the
    // user's slices first and skip the call when the server already matches the
    // intent, so a "save" click can never delete an existing slice.
    return this.ensureLoaded().pipe(
      switchMap(() => {
        if (this._hasSignature(slice.signature) === add) {
          return of({ added: add });
        }
        return this.api.toggle(slice).pipe(
          map((result) => {
            if (result === null) {
              const existing = this.store
                .selectSnapshot(LibraryState.slices)
                .find((s) => s.signature === slice.signature);
              if (existing) {
                this.store.dispatch(new LibraryAction.RemoveSlice(existing.id));
              }
              return { added: false };
            }
            this.store.dispatch(new LibraryAction.UpsertSlice(result));
            return { added: true };
          }),
        );
      }),
    );
  }

  private _hasSignature(signature: string): boolean {
    return this.store.selectSnapshot(LibraryState.signatures).includes(signature);
  }

  remove(sliceId: string): Observable<void> {
    if (!this.isApiMode) {
      this.store.dispatch(new LibraryAction.RemoveSlice(sliceId));
      return of(undefined);
    }

    return this.api.delete(sliceId).pipe(
      tap(() => this.store.dispatch(new LibraryAction.RemoveSlice(sliceId))),
      map(() => undefined),
    );
  }

  toggleFavorite(sliceId: string): Observable<void> {
    const slice = this.store.selectSnapshot(LibraryState.slices).find((s) => s.id === sliceId);
    if (!slice) {
      return of(undefined);
    }

    if (!this.isApiMode) {
      this.store.dispatch(new LibraryAction.ToggleFavorite(sliceId));
      return of(undefined);
    }

    return this.api.update(sliceId, { fav: !slice.fav }).pipe(
      tap((updated) => this.store.dispatch(new LibraryAction.UpsertSlice(updated))),
      map(() => undefined),
    );
  }

  updatePreview(
    sliceId: string,
    previewSeries: SlicePreviewSeries[],
    datasetBuiltAt: string,
  ): Observable<void> {
    if (!this.isApiMode) {
      this.store.dispatch(new LibraryAction.UpdateSlicePreview(sliceId, previewSeries, datasetBuiltAt));
      return of(undefined);
    }

    return this.api.update(sliceId, { previewSeries, datasetBuiltAt }).pipe(
      tap((updated) => this.store.dispatch(new LibraryAction.UpsertSlice(updated))),
      map(() => undefined),
      catchError((error) => {
        console.warn('[LibrarySlicePersistence] Preview sync failed', sliceId, error);
        this.store.dispatch(new LibraryAction.UpdateSlicePreview(sliceId, previewSeries, datasetBuiltAt));
        return of(undefined);
      }),
    );
  }

  private _startLoad(): Observable<void> {
    if (!this.isApiMode) {
      return of(undefined);
    }

    const load$: Observable<void> = this._load(this._session).pipe(
      catchError((error) => {
        if (this._loaded$ === load$) {
          this._loaded$ = null;
        }
        return throwError(() => error);
      }),
      shareReplay(1),
    );
    this._loaded$ = load$;
    return load$;
  }

  /** Fetch the user's slices, migrate local-only ones, and replace the store's slices. */
  private _load(session: number): Observable<void> {
    const localSlices = this._readLocalSlices();
    const setSlices = (slices: DataSlice[]) => {
      if (session === this._session) {
        this.store.dispatch(new LibraryAction.SetSlices(slices));
      }
    };

    return this.api.fetchAll().pipe(
      switchMap((apiSlices) => {
        const apiSignatures = new Set(apiSlices.map((s) => s.signature));
        const toMigrate = localSlices.filter((s) => !apiSignatures.has(s.signature));

        if (toMigrate.length === 0) {
          setSlices(apiSlices);
          return of(undefined);
        }

        return forkJoin(toMigrate.map((slice) => this.api.toggle(slice))).pipe(
          switchMap(() => this.api.fetchAll()),
          tap((allSlices) => setSlices(allSlices)),
          map(() => undefined),
          catchError((error) => {
            console.warn('[LibrarySlicePersistence] Migration failed, using API slices only', error);
            setSlices(apiSlices);
            return of(undefined);
          }),
        );
      }),
      tap(() => this._clearLocalStorage()),
    );
  }

  private _readLocalSlices(): DataSlice[] {
    try {
      const raw = localStorage.getItem(LIBRARY_STATE);
      if (!raw) {
        return [];
      }
      const parsed = JSON.parse(raw) as { slices?: DataSlice[] };
      return Array.isArray(parsed?.slices) ? parsed.slices : [];
    } catch {
      return [];
    }
  }

  private _clearLocalStorage(): void {
    try {
      localStorage.removeItem(LIBRARY_STATE);
    } catch {
      /* ignore */
    }
  }
}
